import {
    ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, ViewEncapsulation,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { first } from 'rxjs/operators';

import { PriceCurrenciesFormatted } from '@api/prices/prices.model';
import { getPriceFormattedWithAlternates } from '@core/infrastructure/state/core.selectors';
import { PriceStyles, PricesClasses } from '@shared/prices/models/product-prices.model';

@Component({
    selector: 'formatted-price',
    templateUrl: './formatted-price.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./formatted-price.component.scss'],
    // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
    encapsulation: ViewEncapsulation.None,
})

/**
 * Componente para mostrar precios formateados
 */
export class FormattedPriceComponent implements OnChanges {
    @Input() public readonly price: number;

    @Input() public readonly secondaryPrice: number;

    @Input() public readonly terciaryPrice: number;

    @Input() public readonly lastReducedPrice: number;

    @Input() public readonly options: {
        showAlternateCurrencies?: boolean;
        fromShopToLook?: boolean;
        noBreakPrice?: boolean;
        pricesInTwoColumns?: boolean;
    };

    @Input() public readonly pricesClasses: PricesClasses;

    @Input() public readonly pricesStyles: {
        primaryPrice: Record<string, any>;
        secondaryPrice?: Record<string, any>;
        alternativeCurrencies?: Record<string, any>;
        cmsStyles: PriceStyles;
    };

    @Input() public readonly primaryText: string;

    @Input() public readonly secondaryText: string;

    @Input() public readonly currency: string = '';

    @Input() public isPageProduct: boolean = false;

    @Input() public isPlp: boolean = false;

    private readonly _pricesFormatted$: BehaviorSubject<PriceCurrenciesFormatted> = new BehaviorSubject(null);

    private readonly _secondaryPricesFormatted$: BehaviorSubject<PriceCurrenciesFormatted> = new BehaviorSubject(null);

    private readonly _terciaryPricesFormatted$: BehaviorSubject<PriceCurrenciesFormatted> = new BehaviorSubject(null);

    private readonly _lastReducedPriceFormatted$: BehaviorSubject<PriceCurrenciesFormatted> = new BehaviorSubject(null);

    public pricesFormatted$: Observable<PriceCurrenciesFormatted> = this._pricesFormatted$.asObservable();

    public secondaryPricesFormatted$: Observable<PriceCurrenciesFormatted> = this._secondaryPricesFormatted$.asObservable();

    public terciaryPricesFormatted$: Observable<PriceCurrenciesFormatted> = this._terciaryPricesFormatted$.asObservable();

    public lastReducedPriceFormatted$: Observable<
    PriceCurrenciesFormatted
    > = this._lastReducedPriceFormatted$.asObservable();

    constructor(private readonly store: Store) {}

    public ngOnChanges(changes: SimpleChanges): void {
        // Precio primario
        if (
            changes.price
            && (
                changes.price?.currentValue !== changes.price?.previousValue
                || changes.pricesClasses?.currentValue !== changes.pricesClasses?.previousValue
                || changes.primaryText?.currentValue !== changes.primaryText?.previousValue
            )

        ) {
            this.store.select(getPriceFormattedWithAlternates(changes.price.currentValue, this.currency)).pipe(
                first()
            ).subscribe((priceCurrency: PriceCurrenciesFormatted) => {
                this._pricesFormatted$.next(priceCurrency);
            });
        }

        // Precio secundario
        // TODO ECOMDUTI-21031 estamos conviertiendo los NaN a undefined para que no pinte los precios anteriores.
        // Habrá que modificar el transformer que setea los precios para que no haya NaNs
        if (
            changes.secondaryPrice
            && (
                changes.secondaryPrice?.currentValue !== changes.secondaryPrice?.previousValue
                || changes.pricesClasses?.currentValue !== changes.pricesClasses?.previousValue
                || changes.secondaryText?.currentValue !== changes.secondaryText?.previousValue
            )
        ) {
            if (changes.secondaryPrice?.currentValue) {
                this.store.select(getPriceFormattedWithAlternates(changes.secondaryPrice.currentValue, this.currency)).pipe(
                    first()
                ).subscribe((priceCurrency: PriceCurrenciesFormatted) => {
                    this._secondaryPricesFormatted$.next(priceCurrency);
                });
            } else {
                this._secondaryPricesFormatted$.next(null);
            }
        }

        const terciaryPrice = changes.terciaryPrice;
        const lastReducedPrice = changes.lastReducedPrice;
        const equalPrices = terciaryPrice?.currentValue === lastReducedPrice?.currentValue;

        if (!!terciaryPrice && terciaryPrice.currentValue !== terciaryPrice.previousValue && !equalPrices) {
            if (terciaryPrice.currentValue && terciaryPrice.currentValue !== 0) {
                this.store.select(getPriceFormattedWithAlternates(terciaryPrice.currentValue)).pipe(
                    first()
                ).subscribe((priceCurrency: PriceCurrenciesFormatted) => {
                    this._terciaryPricesFormatted$.next(priceCurrency);
                });
            } else {
                this._terciaryPricesFormatted$.next(null);
            }
        }

        if (!!lastReducedPrice && lastReducedPrice.currentValue !== lastReducedPrice.previousValue) {
            if (lastReducedPrice.currentValue && lastReducedPrice.currentValue !== 0) {
                this.store.select(getPriceFormattedWithAlternates(lastReducedPrice.currentValue)).pipe(
                    first()
                ).subscribe((priceCurrency: PriceCurrenciesFormatted) => {
                    this._lastReducedPriceFormatted$.next(priceCurrency);
                });
            } else {
                this._lastReducedPriceFormatted$.next(null);
            }
        }
    }
}
